import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Calendar,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Clock,
  FileText,
  Layers,
  Mic,
  Phone,
  Play,
  Settings,
  Sparkles,
  User,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@/components/ui/avatar'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'
import { api } from '@/lib/api'
import {
  AGENT_TEMPLATES,
  DEFAULT_TEMPLATE_CONFIG,
  TEMPLATE_DOMAINS,
  getTemplatesByDomain,
  type AgentTemplate,
  type TemplateConfig,
  type TemplateDomain,
} from '@/lib/agentTemplates'
import { useElevenLabsVoices, type ElevenLabsVoice } from '@/api/aiAgents'
import { VoiceSelectionModal } from './VoiceSelectionModal'

type Step = 'template' | 'config' | 'preview'

// Schéma RHF — couvre tous les champs du wizard (config step 2 + overrides expert step 3).
const wizardSchema = z.object({
  agent_name: z.string().trim().min(1, 'agent_name_required'),
  company_name: z.string().trim().min(1, 'company_name_required'),
  language: z.enum(['fr', 'en']),
  tone: z.enum(['pro', 'warm', 'brief']),
  voice_id: z.string().min(1, 'voice_required'),
  minutes_advance: z.number().int().min(0).max(60),
  max_retries: z.number().int().min(1).max(3),
  offer_reschedule: z.boolean(),
  closing_phrase: z.string(),
  // Mode expert (étape 3) — quand non vides, ces valeurs remplacent le prompt généré.
  first_message_override: z.string(),
  system_prompt_override: z.string(),
})
type WizardValues = z.infer<typeof wizardSchema>

interface Props {
  isOpen: boolean
  onClose: () => void
  companyName: string
  onComplete: (config: {
    template_id: string
    template_config: TemplateConfig
    first_message: string
    system_prompt: string
    voice_id: string
    language: string
  }) => void
}

/** Filtre les voix par langue (compatible labels Next). */
function filterVoicesByLanguage(voices: ElevenLabsVoice[], language: string) {
  if (language === 'fr') {
    return voices.filter(
      (v) =>
        v.labels?.language === 'fr' ||
        v.labels?.language === 'french' ||
        v.labels?.language === 'fr-CA' ||
        v.labels?.language === 'fr-FR',
    )
  }
  if (language === 'en') {
    return voices.filter(
      (v) =>
        v.labels?.language === 'en' ||
        v.labels?.language === 'english' ||
        v.labels?.language === 'en-US' ||
        v.labels?.language === 'en-GB' ||
        v.labels?.language === 'en-CA',
    )
  }
  return voices
}

export function TemplateWizardModal({
  isOpen,
  onClose,
  companyName,
  onComplete,
}: Props) {
  const { t } = useTranslation()
  const { data: voicesData = [] } = useElevenLabsVoices()
  // A new agent: only the offered voices, not those already set on other agents.
  const allVoices = voicesData.filter((v) => !v.en_place)

  const [step, setStep] = useState<Step>('template')
  const [selectedDomain, setSelectedDomain] = useState<TemplateDomain | 'all'>('all')
  const [selectedTemplate, setSelectedTemplate] = useState<AgentTemplate>(AGENT_TEMPLATES[0])
  const [isPromptExpanded, setIsPromptExpanded] = useState(false)
  const [isEditingPrompt, setIsEditingPrompt] = useState(false)
  const [isVoiceModalOpen, setIsVoiceModalOpen] = useState(false)

  const form = useForm<WizardValues>({
    resolver: zodResolver(wizardSchema),
    defaultValues: {
      agent_name: DEFAULT_TEMPLATE_CONFIG.agent_name,
      company_name: companyName,
      language: 'fr',
      tone: DEFAULT_TEMPLATE_CONFIG.tone,
      voice_id: '',
      minutes_advance: DEFAULT_TEMPLATE_CONFIG.minutes_advance,
      max_retries: DEFAULT_TEMPLATE_CONFIG.max_retries,
      offer_reschedule: DEFAULT_TEMPLATE_CONFIG.offer_reschedule,
      closing_phrase: DEFAULT_TEMPLATE_CONFIG.closing_phrase ?? '',
      first_message_override: '',
      system_prompt_override: '',
    },
  })
  const { control, watch, setValue, register, trigger, getValues, formState } = form

  const language = watch('language')
  const tone = watch('tone')
  const voiceId = watch('voice_id')

  // Reset à l'ouverture (companyName peut avoir changé).
  useEffect(() => {
    if (isOpen) {
      form.reset({
        agent_name: '',
        company_name: companyName,
        language: 'fr',
        tone: 'pro',
        voice_id: '',
        minutes_advance: DEFAULT_TEMPLATE_CONFIG.minutes_advance,
        max_retries: DEFAULT_TEMPLATE_CONFIG.max_retries,
        offer_reschedule: DEFAULT_TEMPLATE_CONFIG.offer_reschedule,
        closing_phrase: DEFAULT_TEMPLATE_CONFIG.closing_phrase ?? '',
        first_message_override: '',
        system_prompt_override: '',
      })
      setStep('template')
      setIsEditingPrompt(false)
      setSelectedDomain('all')
      setSelectedTemplate(AGENT_TEMPLATES[0])
    }
  }, [isOpen, companyName, form])

  // Auto-sélection d'une voix compatible quand la langue change ou que les voix arrivent.
  useEffect(() => {
    if (!allVoices.length) return
    const compatible = filterVoicesByLanguage(allVoices, language)
    const stillOk = compatible.some((v) => v.voice_id === voiceId)
    if (!stillOk && compatible.length > 0) {
      setValue('voice_id', compatible[0].voice_id, { shouldDirty: true })
    } else if (!stillOk && !compatible.length && allVoices.length > 0) {
      setValue('voice_id', allVoices[0].voice_id, { shouldDirty: true })
    }
  }, [language, allVoices, voiceId, setValue])

  const filteredVoices = filterVoicesByLanguage(allVoices, language)
  const selectedVoice = filteredVoices.find((v) => v.voice_id === voiceId)

  /** Génère le prompt courant via le template sélectionné. */
  const generated = (() => {
    const cfg = getValues()
    const tplConfig: TemplateConfig = {
      agent_name: cfg.agent_name,
      company_name: cfg.company_name,
      tone: cfg.tone,
      minutes_advance: cfg.minutes_advance,
      offer_reschedule: cfg.offer_reschedule,
      max_retries: cfg.max_retries,
      closing_phrase: cfg.closing_phrase,
      language: cfg.language,
    }
    return selectedTemplate.generator(tplConfig)
  })()

  const handleNextFromConfig = async () => {
    const ok = await trigger([
      'agent_name',
      'company_name',
      'voice_id',
      'language',
      'tone',
    ])
    if (ok) setStep('preview')
  }

  const handleComplete = () => {
    const cfg = getValues()
    // En mode expert, les overrides remplacent intégralement les prompts générés.
    const useOverride = isEditingPrompt
    onComplete({
      template_id: selectedTemplate.id,
      template_config: {
        agent_name: cfg.agent_name,
        company_name: cfg.company_name,
        tone: cfg.tone,
        minutes_advance: cfg.minutes_advance,
        offer_reschedule: cfg.offer_reschedule,
        max_retries: cfg.max_retries,
        closing_phrase: cfg.closing_phrase,
        language: cfg.language,
      },
      first_message: useOverride
        ? cfg.first_message_override || generated.first_message
        : generated.first_message,
      system_prompt: useOverride
        ? cfg.system_prompt_override || generated.system_prompt
        : generated.system_prompt,
      voice_id: cfg.voice_id,
      language: cfg.language,
    })
  }

  const playTTSPreview = async () => {
    if (!voiceId || !generated.first_message) return
    try {
      const { data } = await api.post<Blob>(
        '/api/elevenlabs/tts-preview/',
        { text: generated.first_message, voice_id: voiceId },
        { responseType: 'blob' },
      )
      const url = URL.createObjectURL(data)
      const audio = new Audio(url)
      void audio.play()
      audio.onended = () => URL.revokeObjectURL(url)
    } catch {
      toast.error(t('manager.aiAgents.wizard.ttsError'))
    }
  }

  const getDomainName = (id: string) =>
    t(`manager.aiAgents.domains.${id === 'real_estate' ? 'realEstate' : id}`)

  const getTemplateName = (id: string) => t(`manager.aiAgents.templates.${id}`)
  const getTemplateDescription = (id: string) =>
    t(`manager.aiAgents.templates.${id}Desc`)

  const toneLabel = (k: 'pro' | 'warm' | 'brief') =>
    t(`manager.aiAgents.wizard.${k === 'pro' ? 'professional' : k === 'warm' ? 'warmTone' : 'direct'}`)

  const variableInfo = (variable: string): { label: string; icon: React.ReactNode } => {
    const M: Record<string, { key: string; icon: React.ReactNode }> = {
      agent_name: { key: 'varAgentName', icon: <User className="h-3.5 w-3.5" /> },
      client_name: { key: 'varClientName', icon: <User className="h-3.5 w-3.5" /> },
      appointment_date: { key: 'varAppointmentDate', icon: <Calendar className="h-3.5 w-3.5" /> },
      appointment_time: { key: 'varAppointmentTime', icon: <Clock className="h-3.5 w-3.5" /> },
      appointment_notes: { key: 'varAppointmentNotes', icon: <FileText className="h-3.5 w-3.5" /> },
      client_phone: { key: 'varClientPhone', icon: <Phone className="h-3.5 w-3.5" /> },
      phone_number: { key: 'varClientPhone', icon: <Phone className="h-3.5 w-3.5" /> },
    }
    const found = M[variable]
    return found
      ? { label: t(`manager.aiAgents.wizard.${found.key}`), icon: found.icon }
      : { label: variable, icon: <FileText className="h-3.5 w-3.5" /> }
  }

  const errMsg = (key?: string) =>
    key ? t(`manager.aiAgents.errors.${key}`) : null

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] p-0 gap-0 flex flex-col border-0 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="relative px-6 pt-6 pb-4 bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-950 border-b border-slate-100 dark:border-slate-800">
          <DialogHeader className="p-0">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-blue-600 shadow-lg shadow-blue-600/25">
                {step === 'template' && <Layers className="h-5 w-5 text-white" />}
                {step === 'config' && <Settings className="h-5 w-5 text-white" />}
                {step === 'preview' && <Sparkles className="h-5 w-5 text-white" />}
              </div>
              <div>
                <DialogTitle className="text-lg font-semibold text-slate-900 dark:text-white">
                  {step === 'template' && t('manager.aiAgents.wizard.chooseTemplate')}
                  {step === 'config' && t('manager.aiAgents.wizard.customizeAgent')}
                  {step === 'preview' && t('manager.aiAgents.wizard.finalPreview')}
                </DialogTitle>
                <DialogDescription className="sr-only">
                  {t('manager.aiAgents.wizard.wizardDescription')}
                </DialogDescription>
              </div>
            </div>

            <div className="flex items-center gap-2 ml-[52px]">
              {(['template', 'config', 'preview'] as Step[]).map((s, i) => {
                const active = step === s || (step === 'config' && i < 2) || step === 'preview'
                return (
                  <div
                    key={s}
                    className={cn(
                      'h-1.5 w-12 rounded-full transition-colors',
                      active ? 'bg-blue-600' : 'bg-slate-200 dark:bg-slate-700',
                    )}
                  />
                )
              })}
              <span className="text-xs text-slate-500 dark:text-slate-400 ml-2">
                {t('manager.aiAgents.wizard.step')}{' '}
                {step === 'template' ? '1' : step === 'config' ? '2' : '3'}/3
              </span>
            </div>
          </DialogHeader>
        </div>

        <div className="p-6 flex-1 overflow-y-auto">
          {/* Étape 1 : sélection du template */}
          {step === 'template' && (
            <div className="space-y-5">
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                {t('manager.aiAgents.wizard.selectUseCase')}
              </p>

              <div className="flex flex-wrap gap-2 mb-5">
                <button
                  type="button"
                  onClick={() => setSelectedDomain('all')}
                  className={cn(
                    'px-4 py-2 rounded-lg text-sm font-medium transition-all',
                    selectedDomain === 'all'
                      ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 shadow-md'
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700',
                  )}
                >
                  {t('manager.aiAgents.wizard.all')}
                </button>
                {Object.values(TEMPLATE_DOMAINS).map((domain) => {
                  const inDomain = AGENT_TEMPLATES.filter((tp) => tp.domain === domain.id)
                  if (inDomain.length === 0) return null
                  const isSel = selectedDomain === domain.id
                  return (
                    <button
                      key={domain.id}
                      type="button"
                      onClick={() => setSelectedDomain(domain.id as TemplateDomain)}
                      className={cn(
                        'px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2',
                        isSel
                          ? 'bg-blue-600 text-white shadow-md shadow-blue-500/25'
                          : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700',
                      )}
                    >
                      <span>{getDomainName(domain.id)}</span>
                      <span
                        className={cn(
                          'text-xs px-1.5 py-0.5 rounded',
                          isSel ? 'bg-white/20' : 'bg-slate-200 dark:bg-slate-700',
                        )}
                      >
                        {inDomain.length}
                      </span>
                    </button>
                  )
                })}
              </div>

              <div className="space-y-5">
                {(() => {
                  const byDomain = getTemplatesByDomain()
                  const domainsToShow =
                    selectedDomain === 'all'
                      ? (Object.keys(TEMPLATE_DOMAINS) as TemplateDomain[])
                      : [selectedDomain]
                  return domainsToShow.map((domainId) => {
                    const domain = TEMPLATE_DOMAINS[domainId]
                    const templates = byDomain[domainId]
                    if (templates.length === 0) return null
                    return (
                      <div key={domainId} className="space-y-3">
                        {selectedDomain === 'all' && (
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-semibold text-slate-900 dark:text-white">
                              {getDomainName(domain.id)}
                            </h4>
                            <span className="text-xs text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full">
                              {templates.length}
                            </span>
                          </div>
                        )}

                        <div className="space-y-3">
                          {templates.map((template) => {
                            const isSelected = selectedTemplate.id === template.id
                            return (
                              <button
                                key={template.id}
                                type="button"
                                onClick={() => setSelectedTemplate(template)}
                                className={cn(
                                  'group w-full p-4 rounded-xl text-left transition-all',
                                  isSelected
                                    ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-950/30 ring-2 ring-blue-500/20'
                                    : 'border border-slate-200 dark:border-slate-700 hover:border-slate-300 bg-white dark:bg-slate-800/50 hover:shadow-md',
                                )}
                              >
                                <div className="flex items-start gap-4">
                                  <div className="flex-1 min-w-0">
                                    <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-1">
                                      {getTemplateName(template.id)}
                                    </h3>
                                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-3 line-clamp-2">
                                      {getTemplateDescription(template.id)}
                                    </p>
                                    <div className="flex flex-wrap items-center gap-2">
                                      <span className="text-[10px] font-medium uppercase tracking-wider px-2 py-1 rounded-md bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300">
                                        {getDomainName(domain.id)}
                                      </span>
                                      <span className="text-[10px] px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-md font-mono">
                                        {template.requiredVariables.length} var
                                      </span>
                                      {template.recommendedActions.length > 0 && (
                                        <span className="text-[10px] px-2 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-md">
                                          {template.recommendedActions.length} action
                                          {template.recommendedActions.length > 1 ? 's' : ''}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div
                                    className={cn(
                                      'flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all',
                                      isSelected
                                        ? 'border-blue-600 bg-blue-600'
                                        : 'border-slate-300 dark:border-slate-600 group-hover:border-slate-400',
                                    )}
                                  >
                                    {isSelected && (
                                      <svg
                                        className="w-3 h-3 text-white"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={3}
                                          d="M5 13l4 4L19 7"
                                        />
                                      </svg>
                                    )}
                                  </div>
                                </div>
                              </button>
                            )
                          })}
                        </div>
                      </div>
                    )
                  })
                })()}
              </div>

              {AGENT_TEMPLATES.length === 0 && (
                <div className="text-center py-12 text-slate-500 dark:text-slate-400">
                  <Layers className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>{t('manager.aiAgents.wizard.noTemplateAvailable')}</p>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
                <Button type="button" variant="outline" onClick={onClose}>
                  {t('manager.aiAgents.wizard.cancelBtn')}
                </Button>
                <Button
                  type="button"
                  onClick={() => setStep('config')}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {t('manager.aiAgents.wizard.next')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Étape 2 : configuration */}
          {step === 'config' && (
            <div className="space-y-5">
              <div>
                <h4 className="font-medium text-slate-900 dark:text-white mb-4">
                  {t('manager.aiAgents.wizard.agentIdentity')}
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="agent_name">
                      {t('manager.aiAgents.wizard.agentNameLabel')}
                    </Label>
                    <Input
                      id="agent_name"
                      placeholder={t('manager.aiAgents.wizard.agentNamePlaceholder')}
                      className="mt-2"
                      {...register('agent_name')}
                    />
                    {formState.errors.agent_name && (
                      <p className="text-xs text-red-500 mt-1">
                        {errMsg(formState.errors.agent_name.message)}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      {t('manager.aiAgents.wizard.agentNameDescription')}
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="language">
                      {t('manager.aiAgents.wizard.agentLanguage')}
                    </Label>
                    <Controller
                      control={control}
                      name="language"
                      render={({ field }) => (
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger id="language" className="mt-2">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="fr">
                              {t('manager.aiAgents.wizard.french')}
                            </SelectItem>
                            <SelectItem value="en">
                              {t('manager.aiAgents.wizard.english')}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                </div>

                {/* Voice picker */}
                <div className="mt-4">
                  <Label>{t('manager.aiAgents.wizard.agentVoice')}</Label>
                  {selectedVoice ? (
                    <div className="mt-2 border dark:border-slate-700 rounded-lg p-4 bg-white dark:bg-slate-900 hover:border-blue-300 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-12 w-12">
                            <AvatarImage
                              src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${selectedVoice.name}`}
                            />
                            <AvatarFallback>
                              <User className="h-6 w-6" />
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">
                              {selectedVoice.name}
                            </div>
                            <div className="text-sm text-gray-500 mt-0.5 capitalize">
                              {[selectedVoice.labels?.accent, selectedVoice.labels?.gender]
                                .filter(Boolean)
                                .join(' • ')}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {selectedVoice.preview_url && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation()
                                if (selectedVoice.preview_url) {
                                  const audio = new Audio(selectedVoice.preview_url)
                                  void audio.play()
                                }
                              }}
                              className="rounded-full"
                            >
                              <Play className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setIsVoiceModalOpen(true)}
                          >
                            {t('manager.aiAgents.wizard.changeVoice')}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsVoiceModalOpen(true)}
                        className="w-full gap-2"
                      >
                        <Mic className="h-4 w-4" />
                        {t('manager.aiAgents.wizard.chooseVoice')}
                      </Button>
                      {filteredVoices.length > 0 && (
                        <p className="text-xs text-gray-500 mt-2 text-center">
                          {filteredVoices.length}{' '}
                          {t('manager.aiAgents.wizard.voicesAvailable')}
                        </p>
                      )}
                    </div>
                  )}
                  {formState.errors.voice_id && (
                    <p className="text-xs text-red-500 mt-1">
                      {errMsg(formState.errors.voice_id.message)}
                    </p>
                  )}
                </div>

                {/* Tone pills */}
                <div className="mt-4">
                  <Label>{t('manager.aiAgents.wizard.agentTone')}</Label>
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {(['pro', 'warm', 'brief'] as const).map((toneKey) => (
                      <button
                        key={toneKey}
                        type="button"
                        onClick={() => setValue('tone', toneKey, { shouldDirty: true })}
                        className={cn(
                          'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                          tone === toneKey
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-700',
                        )}
                      >
                        {toneLabel(toneKey)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Company name */}
                <div className="mt-4">
                  <Label htmlFor="company_name">
                    {t('manager.aiAgents.wizard.companyName')}
                  </Label>
                  <Input
                    id="company_name"
                    placeholder={t('manager.aiAgents.wizard.companyNamePlaceholder')}
                    className="mt-2"
                    {...register('company_name')}
                  />
                  {formState.errors.company_name && (
                    <p className="text-xs text-red-500 mt-1">
                      {errMsg(formState.errors.company_name.message)}
                    </p>
                  )}
                </div>
              </div>

              <div className="border-t dark:border-slate-800" />

              {/* Advanced settings */}
              <Collapsible defaultOpen={false}>
                <CollapsibleTrigger
                  asChild
                  className="flex items-center justify-between w-full"
                >
                  <button
                    type="button"
                    className="w-full p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors flex items-center justify-between"
                  >
                    <span className="flex items-center gap-2">
                      <Settings className="h-4 w-4 text-gray-500" />
                      <span className="font-medium text-gray-900 dark:text-white">
                        {t('manager.aiAgents.wizard.advancedSettings')}
                      </span>
                    </span>
                    <ChevronDown className="h-4 w-4 text-gray-500" />
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="mt-4 p-4 bg-gray-50 dark:bg-slate-800/50 rounded-lg space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="minutes_advance">
                          {t('manager.aiAgents.wizard.minutesAdvance')}
                        </Label>
                        <Input
                          id="minutes_advance"
                          type="number"
                          min={0}
                          max={60}
                          className="mt-2"
                          {...register('minutes_advance', { valueAsNumber: true })}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          {t('manager.aiAgents.wizard.minutesAdvanceDesc')}
                        </p>
                      </div>

                      <div>
                        <Label htmlFor="max_retries">
                          {t('manager.aiAgents.wizard.maxRetries')}
                        </Label>
                        <Input
                          id="max_retries"
                          type="number"
                          min={1}
                          max={3}
                          className="mt-2"
                          {...register('max_retries', { valueAsNumber: true })}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          {t('manager.aiAgents.wizard.maxRetriesDesc')}
                        </p>
                      </div>
                    </div>

                    <Controller
                      control={control}
                      name="offer_reschedule"
                      render={({ field }) => (
                        <div className="flex items-center justify-between rounded-lg border dark:border-slate-700 p-4 bg-white dark:bg-slate-900">
                          <Label
                            htmlFor="offer_reschedule"
                            className="flex flex-col space-y-1 cursor-pointer"
                          >
                            <span className="font-medium">
                              {t('manager.aiAgents.wizard.offerReschedule')}
                            </span>
                            <span className="text-xs text-gray-500 font-normal">
                              {t('manager.aiAgents.wizard.offerRescheduleDesc')}
                            </span>
                          </Label>
                          <Switch
                            id="offer_reschedule"
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </div>
                      )}
                    />

                    <div>
                      <Label htmlFor="closing_phrase">
                        {t('manager.aiAgents.wizard.closingPhrase')}
                      </Label>
                      <Textarea
                        id="closing_phrase"
                        rows={2}
                        placeholder={t('manager.aiAgents.wizard.closingPhrasePlaceholder')}
                        className="mt-2"
                        {...register('closing_phrase')}
                      />
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>

              <div className="flex justify-between gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep('template')}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  {t('manager.aiAgents.wizard.back')}
                </Button>
                <Button
                  type="button"
                  onClick={() => void handleNextFromConfig()}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {t('manager.aiAgents.wizard.preview')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Étape 3 : preview */}
          {step === 'preview' && (
            <div className="space-y-6">
              {!isEditingPrompt && (
                <>
                  <div className="border-b dark:border-slate-800 pb-4">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-12 w-12">
                        <AvatarImage
                          src={
                            selectedVoice
                              ? `https://api.dicebear.com/7.x/avataaars/svg?seed=${selectedVoice.name}`
                              : undefined
                          }
                        />
                        <AvatarFallback>
                          <User className="h-6 w-6" />
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="font-semibold text-lg text-gray-900 dark:text-white">
                          {getValues('agent_name') || 'Agent'}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-slate-400 mt-0.5">
                          {getTemplateName(selectedTemplate.id)} • {toneLabel(tone)}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-3">
                      {t('manager.aiAgents.wizard.introSimulation')}
                    </h4>
                    <div className="flex items-start gap-3">
                      <Avatar className="h-8 w-8 flex-shrink-0">
                        <AvatarImage
                          src={
                            selectedVoice
                              ? `https://api.dicebear.com/7.x/avataaars/svg?seed=${selectedVoice.name}`
                              : undefined
                          }
                        />
                        <AvatarFallback>
                          <User className="h-4 w-4" />
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 flex items-start gap-2">
                        <div className="bg-blue-50 dark:bg-blue-500/10 rounded-2xl rounded-tl-sm p-4 border border-blue-100 dark:border-blue-500/30 flex-1">
                          <p className="text-sm text-gray-900 dark:text-slate-200">
                            {generated.first_message}
                          </p>
                        </div>
                        {voiceId && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => void playTTSPreview()}
                            className="gap-2 text-xs flex-shrink-0"
                          >
                            <Play className="h-3.5 w-3.5" />
                            {t('manager.aiAgents.wizard.listenIntro')}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg border dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50 p-4">
                    <h4 className="font-medium mb-3 text-gray-900 dark:text-white">
                      {t('manager.aiAgents.wizard.agentKnows')}
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedTemplate.requiredVariables.map((variable) => {
                        const { label, icon } = variableInfo(variable)
                        return (
                          <div
                            key={variable}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg text-sm text-gray-700 dark:text-slate-300"
                          >
                            <span className="text-gray-500">{icon}</span>
                            <span>{label}</span>
                          </div>
                        )
                      })}
                    </div>
                  </div>

                  <div className="rounded-lg border dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50">
                    <button
                      type="button"
                      onClick={() => setIsPromptExpanded(!isPromptExpanded)}
                      className="w-full p-4 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
                    >
                      <h4 className="font-medium text-sm text-gray-700 dark:text-slate-300">
                        {t('manager.aiAgents.wizard.systemPrompt')}
                      </h4>
                      {isPromptExpanded ? (
                        <ChevronUp className="h-4 w-4 text-gray-500" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-gray-500" />
                      )}
                    </button>
                    {isPromptExpanded && (
                      <div className="px-4 pb-4">
                        <pre className="text-xs text-gray-700 dark:text-slate-300 whitespace-pre-wrap font-mono bg-white dark:bg-slate-900 p-4 rounded border dark:border-slate-700 overflow-x-auto">
                          {generated.system_prompt}
                        </pre>
                      </div>
                    )}
                  </div>

                  {selectedTemplate.recommendedActions.length > 0 && (
                    <div className="rounded-lg border border-blue-200 dark:border-blue-500/30 bg-blue-50/50 dark:bg-blue-500/10 p-3">
                      <p className="text-xs text-blue-800 dark:text-blue-200">
                        <span className="font-medium">
                          {t('manager.aiAgents.wizard.nextStepNote')}
                        </span>{' '}
                        {t('manager.aiAgents.wizard.nextStepDesc', {
                          actions: selectedTemplate.recommendedActions
                            .join(', ')
                            .replace(/_/g, ' '),
                        })}
                      </p>
                    </div>
                  )}

                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      // Initialise les overrides RHF avec le contenu généré pour permettre
                      // une édition directe — ils restent ensuite la source de vérité.
                      setValue('first_message_override', generated.first_message, {
                        shouldDirty: true,
                      })
                      setValue('system_prompt_override', generated.system_prompt, {
                        shouldDirty: true,
                      })
                      setIsEditingPrompt(true)
                    }}
                    className="w-full text-sm"
                  >
                    {t('manager.aiAgents.wizard.editPrompt')}
                  </Button>
                </>
              )}

              {isEditingPrompt && (
                <>
                  <div className="rounded-lg border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-4">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium">
                          {t('manager.aiAgents.wizard.expertMode')}
                        </p>
                        <p className="text-xs text-gray-600 dark:text-slate-400 mt-1">
                          {t('manager.aiAgents.wizard.expertModeDesc')}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="first_message_override">
                      {t('manager.aiAgents.wizard.firstMessageLabel')}
                    </Label>
                    <Textarea
                      id="first_message_override"
                      rows={3}
                      className="mt-2 font-mono text-sm"
                      {...register('first_message_override')}
                    />
                  </div>

                  <div>
                    <Label htmlFor="system_prompt_override">
                      {t('manager.aiAgents.wizard.systemPromptLabel')}
                    </Label>
                    <Textarea
                      id="system_prompt_override"
                      rows={15}
                      className="mt-2 font-mono text-sm"
                      {...register('system_prompt_override')}
                    />
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setValue('first_message_override', '', { shouldDirty: true })
                      setValue('system_prompt_override', '', { shouldDirty: true })
                      setIsEditingPrompt(false)
                    }}
                    className="w-full"
                  >
                    {t('manager.aiAgents.wizard.cancelEdits')}
                  </Button>
                </>
              )}

              <div className="flex justify-between gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
                <Button type="button" variant="outline" onClick={() => setStep('config')}>
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  {t('manager.aiAgents.wizard.back')}
                </Button>
                <Button
                  type="button"
                  onClick={handleComplete}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {t('manager.aiAgents.wizard.createAgentBtn')}
                  <CheckCircle className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        <VoiceSelectionModal
          isOpen={isVoiceModalOpen}
          onClose={() => setIsVoiceModalOpen(false)}
          voices={filteredVoices}
          selectedVoiceId={voiceId}
          onSelectVoice={(id) =>
            setValue('voice_id', id, { shouldDirty: true, shouldValidate: true })
          }
        />
      </DialogContent>
    </Dialog>
  )
}
